import {
  CommonEntitiesListResponse,
  CompanyPurchasedSubscriptionExtendedResponse,
  CompanyPurchasedSubscriptionResponse,
} from '@docbay/schemas';
import axiosInstance from './axiosInstance';

export interface AddOn {
  id: number;
  name: string;
  description: string;
  price: number;
  currency: string;
  stripe_price_id: string;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  billing_cycle: string;
  price: number;
  currency: string;
  stripe_price_id: string;
  perks: Record<string, any>;
  tag: string;
  addOns: AddOn[];
}

export interface CheckoutPayload {
  planId: number;
  patientId: string;
  addOnIds: number[];
}

export interface CheckoutResponse {
  url: string;
  success: boolean;
  message: string;
}

export interface ClinicSubscriptionSelectedAddonPayload {
  id: string;
  title: string;
}

export interface ClinicSubscriptionEmployeeAddonPayload {
  addonId: string;
  addonTitle: string;
}

export interface ClinicSubscriptionEmployeePayload {
  patientId: string;
  addons: ClinicSubscriptionEmployeeAddonPayload[];
}

export interface ClinicSubscriptionExcludedEmployeePayload {
  patientId: string;
}

export interface ClinicSubscriptionRequestPayload {
  companyId: string;
  adminId: string;
  planType: string;
  subscriptionId?: string;
  employees: ClinicSubscriptionEmployeePayload[];
  isApplyToAll: boolean;
  excludedEmployees: ClinicSubscriptionExcludedEmployeePayload[];
  selectedAddons: ClinicSubscriptionSelectedAddonPayload[];
  setupIntentId?: string;
}

export type CreateCompanySubscriptionPayload = ClinicSubscriptionRequestPayload;

export interface CreateCompanySubscriptionResponse {
  success: boolean;
  message: string;
  employerSubscriptionId?: number;
  stripeSubscriptionId?: string;
  needsSepaSetup?: boolean;
  sepaClientSecret?: string;
  checkoutUrl?: string;
  checkoutSessionId?: string;
}

export type UpdateCompanySubscriptionPayload = ClinicSubscriptionRequestPayload;

export interface UpdateCompanySubscriptionResponse {
  success: boolean;
  message: string;
  employerSubscriptionId: number;
  stripeSubscriptionId: string;
}

export interface CancelCompanySubscriptionResponse {
  success: boolean;
  message: string;
  employerSubscriptionId: number;
}

export interface CompanyEmployeeSubscriptionAddOn {
  id: number;
  name: string;
  description: string;
  price: number;
  currency: string;
  stripePriceId: string;
  metadata?: Record<string, any>;
}

export interface CompanyEmployeeSubscriptionPatientAddOn {
  patientId: string;
  addons: CompanyEmployeeSubscriptionAddOn[];
}

export interface NextInvoiceLine {
  amount: number;
  currency: string;
  description: string;
  quantity: number;
  period: {
    end: number;
    start: number;
  };
}

export interface NextInvoice {
  object: string;
  id?: string | number;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_address?: string | null;
  period_start: number;
  period_end: number;
  next_payment_attempt?: number;
  lines: {
    object: string;
    has_more: boolean;
    total_count: number;
    data: NextInvoiceLine[];
  };
  total: number;
  subtotal: number;
  subtotal_excluding_tax?: number;
  amount_due: number;
  amount_remaining?: number;
  amount_paid?: number;
  currency: string;
  starting_balance?: number;
  tax?: number | null;
  status?: string;
  [key: string]: any;
}

export interface CompanyEmployeeSubscriptionItem {
  id: number;
  companyId?: string;
  clinicId: string;
  adminId: string;
  planType: 'basicCare' | 'completeCare' | 'pro';
  status: string;
  startDate: string | null;
  endDate: string | null;
  stripeSubscriptionId: string | null;
  cancelledAt: string | null;
  subscription: {
    id: number;
    name: string;
    description: string;
    price: number;
    currency: string;
    stripePriceId: string;
  };
  quantity: number;
  totalPrice: number;
  patientAddons: CompanyEmployeeSubscriptionPatientAddOn[];
  nextInvoice?: NextInvoice | null;
}

export interface CompanyEmployeeSubscriptionsResponse {
  success: boolean;
  message: string;
  employeeSubscription: CompanyEmployeeSubscriptionItem[];
}

export interface CompanySubscriptionDetailEmployee {
  patientId: string;
  addOnIds: number[];
  addOns: Array<{
    id: number;
    name: string;
    description: string;
    price: number;
    currency: string;
    stripePriceId: string;
  }>;
}

export interface CompanySubscriptionDetailsResponse {
  id: number;
  clinicId: string;
  adminId: string;
  planType: 'basicCare' | 'completeCare' | 'pro';
  status: string;
  stripeSubscriptionId: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  startDate: string | null;
  endDate: string | null;
  subscription: {
    id: number;
    name: string;
    description: string;
    price: number;
    currency: string;
    stripePriceId: string;
  };
  employees: CompanySubscriptionDetailEmployee[];
  selectedAddons?: Array<{
    title: string;
  }>;
  isApplyToAll?: boolean;
}

/**
 * SubscriptionsAPI provides methods for interacting with the subscriptions API.
 */
export const SubscriptionsAPI = {
  /**
   * Fetches all available patient subscription plans.
   * @returns A promise that resolves to an array of subscription plans.
   */
  getPatientSubscriptions: () =>
    axiosInstance.get<SubscriptionPlan[]>(
      '/subscriptions/patients?isCompany=true',
    ),

  /**
   * Creates a checkout session for AI subscription.
   * @param payload - The checkout data including planId, patientId, and addOnIds.
   * @returns A promise that resolves to the checkout response with URL.
   */
  createCheckout: (payload: CheckoutPayload) =>
    axiosInstance.post<CheckoutResponse>(
      '/subscriptions/ai-subscription/checkout',
      payload,
    ),

  /**
   * Creates a clinic subscription.
   * @param payload - The clinic subscription data including companyId, planType, employee selections, and apply-to-all settings.
   * @returns A promise that resolves to the create response, which may include SEPA setup requirements.
   */
  createCompanySubscription: (payload: CreateCompanySubscriptionPayload) =>
    axiosInstance.post<CreateCompanySubscriptionResponse>(
      '/subscriptions/company',
      payload,
    ),

  /**
   * Updates an existing clinic subscription.
   * @param id - The employer subscription ID.
   * @param payload - The update data including companyId, planType, employee selections, and apply-to-all settings.
   * @returns A promise that resolves to the update response.
   */
  updateCompanySubscription: (
    id: number,
    payload: UpdateCompanySubscriptionPayload,
  ) =>
    axiosInstance.put<UpdateCompanySubscriptionResponse>(
      `/subscriptions/company/${id}`,
      payload,
    ),

  /**
   * Cancels a clinic subscription.
   * @param id - The employer subscription ID.
   * @returns A promise that resolves to the cancel response.
   */
  cancelCompanySubscription: (id: number) =>
    axiosInstance.delete<CancelCompanySubscriptionResponse>(
      `/subscriptions/company/${id}`,
    ),

  /**
   * Retrieves company subscriptions including assigned patients and add-ons.
   * @param companyId - The company identifier.
   * @param searchTerm - Optional search term to filter subscriptions.
   * @param status - Optional status to filter subscriptions.
   * @returns A promise that resolves to the company subscriptions.
   */
  getCompanyEmployeeSubscriptions: (
    companyId: string,
    searchTerm?: string,
    status?: string,
  ) =>
    axiosInstance.get<CompanyEmployeeSubscriptionsResponse>(
      '/subscriptions/employees',
      {
        params: {
          companyId,
          ...(searchTerm ? { searchTerm } : {}),
          ...(status ? { status } : {}),
        },
      },
    ),

  /**
   * Retrieves detailed information about a specific company subscription.
   * @param id - The clinic subscription ID.
   * @returns A promise that resolves to the clinic subscription details.
   */
  getCompanySubscriptionDetails: (id: number) =>
    axiosInstance.get<CompanySubscriptionDetailsResponse>(
      `/subscriptions/company/${id}/details`,
    ),

  getSubscriptions: (page: number) => {
    return axiosInstance.get<
      CommonEntitiesListResponse<CompanyPurchasedSubscriptionResponse>
    >('/hr-departments/subscriptions/purchased', {
      params: { limit: 10, page },
    });
  },

  /**
   * Retrieves a company purchased subscription by its ID.
   *
   * @param companyPurchasedSubscriptionId - The unique identifier of the company purchased subscription to retrieve.
   * @returns A response object containing the details of the company purchased subscription.
   */
  getSubscriptionById: (companyPurchasedSubscriptionId: string) => {
    return axiosInstance.get<CompanyPurchasedSubscriptionExtendedResponse>(
      `/hr-departments/subscriptions/purchased/${companyPurchasedSubscriptionId}`,
    );
  },

  /**
   * Deletes a company purchased subscription by its ID.
   *
   * @param companyPurchasedSubscriptionId - The unique identifier of the company purchased subscription to delete.
   */
  deleteSubscription: (companyPurchasedSubscriptionId: string) => {
    return axiosInstance.delete(
      `/hr-departments/subscriptions/purchased/${companyPurchasedSubscriptionId}`,
    );
  },
};
